import React, { useEffect, useState } from 'react';
import { Heart, Quote } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const OurStory = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const content = {
    fr: {
      title: "Notre Histoire",
      cyrille: {
        name: "Cyrille",
        paragraphs: [
          "« Quand nos chemins se sont croisés, nous ne cherchions pas l'amour, mais la paix et une direction. Et c'est dans cette recherche que Dieu nous a placés l'un face à l'autre. Nelly, avec sa douceur rare et sa force tranquille, est entrée dans ma vie comme une évidence.",
          "Elle ne crie pas son élégance, elle la porte. Elle ne revendique pas sa force, elle l'incarne. Dans ses silences, j'ai appris l'humilité. Dans son rire, j'ai découvert la joie pure. Dans sa foi inébranlable, j'ai trouvé le courage de croire encore plus grand. »"
        ]
      },
      nelly: {
        name: "Nelly",
        paragraphs: [
          "« Dès nos premiers échanges, j'ai rencontré une âme simple et sincère. Sa façon de penser et de s'exprimer m'a vite touchée, bien avant que je comprenne la place qu'il prendrait dans ma vie.",
          "Avec le temps, j'ai vu au-delà de son intelligence : un cœur généreux, patient et attentif. Cyrille sait m'écouter, comprendre mes silences et trouver les mots justes quand ils sont nécessaires.",
          "Sa présence m'a appris que la force peut être douce, que la patience est une forme d'amour, et que marcher avec Dieu rend tout chemin possible. Peu à peu, il est devenu une évidence dans ma vie, un compagnon de foi et d'amour avec qui je veux construire, grandir et espérer. »"
        ]
      },
      conclusion: {
        author: "Cyrille",
        text: "« Ce n'est pas une histoire de coup de foudre, mais de construction consciente. Jour après jour, nous avons choisi de nous comprendre, de nous soutenir et de grandir ensemble. »"
      },
      finalQuote: "Dieu savait exactement ce qu'il faisait."
    },
    en: {
      title: "Our Story",
      cyrille: {
        name: "Cyrille",
        paragraphs: [
          "« When our paths crossed, we weren't looking for love, but for peace and direction. And it was in this search that God placed us face to face. Nelly, with her rare gentleness and quiet strength, entered my life as an obvious truth.",
          "She doesn't proclaim her elegance, she embodies it. She doesn't claim her strength, she lives it. In her silences, I learned humility. In her laughter, I discovered pure joy. In her unwavering faith, I found the courage to believe even greater. »"
        ]
      },
      nelly: {
        name: "Nelly",
        paragraphs: [
          "« From our very first exchanges, I discovered a thoughtful, composed and deeply sincere man. The clarity of his ideas and the consistency of his words quickly caught my attention, long before I understood the place he would take in my life.",
          "Over time, I discovered much more than his intelligence. I discovered his heart: generous, patient and attentive. Cyrille truly knows how to listen, understand even what is not always said, and find the right words when they are most needed.",
          "His presence taught me that strength can be gentle, that patience is a form of love, and that walking with God together makes any path possible. Gradually, he became an obvious truth in my life, a companion in faith and love with whom I wanted to build, grow and hope. »"
        ]
      },
      conclusion: {
        author: "Cyrille",
        text: "« This is not a story of love at first sight, but of conscious building. Day after day, we chose to understand each other, support each other and grow together. »"
      },
      finalQuote: "God knew exactly what He was doing."
    }
  };

  const data = content[language];

  return (
    <div className="min-h-screen pt-16 sm:pt-20 pb-12 sm:pb-20 bg-white">
      {/* ═══════════════════════════════════════════════════════════════
          HEADER
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-gradient-to-b from-lilac-100 via-lilac-200 to-lilac-100 py-20 sm:py-28 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-48 sm:w-72 h-48 sm:h-72 bg-white/30 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 sm:w-72 h-48 sm:h-72 bg-gold-200/20 rounded-full blur-3xl" />
        </div>

        <div className={`container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Heart className="mx-auto text-gold-500 fill-gold-500 w-12 h-12 sm:w-16 sm:h-16 mb-6" />
          <h1 className="font-playfair font-bold text-plum-900 mb-4 sm:mb-6">
            {data.title}
          </h1>
          <div className="divider-gold mt-6 sm:mt-8" />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          CYRILLE'S WORDS
          ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Author Badge */}
            <div className={`flex items-center justify-center mb-8 sm:mb-12 transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="bg-lilac-50 border border-lilac-200 rounded-full px-6 sm:px-8 py-3 shadow-soft">
                <span className="text-plum-900 font-playfair font-bold text-xl sm:text-2xl">
                  {data.cyrille.name}
                </span>
              </div>
            </div>

            {/* Cyrille's Paragraphs */}
            <div className="space-y-6 sm:space-y-8">
              {data.cyrille.paragraphs.map((paragraph, index) => (
                <div
                  key={index}
                  className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${(index + 2) * 150}ms` }}
                >
                  <p className="text-base sm:text-lg text-plum-700 font-montserrat leading-relaxed text-justify">
                    {paragraph}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          DIVIDER
          ═══════════════════════════════════════════════════════════════ */}
      <div className="py-8 sm:py-12 bg-gradient-to-r from-white via-lilac-50 to-white">
        <div className="flex items-center justify-center gap-4">
          <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent to-gold-400" />
          <Heart className="text-gold-500 fill-gold-500 w-6 h-6 sm:w-8 sm:h-8" />
          <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent to-gold-400" />
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          NELLY'S WORDS
          ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Author Badge */}
            <div className={`flex items-center justify-center mb-8 sm:mb-12 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="bg-gold-50 border border-gold-200 rounded-full px-6 sm:px-8 py-3 shadow-soft">
                <span className="text-plum-900 font-playfair font-bold text-xl sm:text-2xl">
                  {data.nelly.name}
                </span>
              </div>
            </div>

            {/* Nelly's Paragraphs */}
            <div className="space-y-6 sm:space-y-8">
              {data.nelly.paragraphs.map((paragraph, index) => (
                <div
                  key={index}
                  className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${(index + 5) * 150}ms` }}
                >
                  <p className="text-base sm:text-lg text-plum-700 font-montserrat leading-relaxed text-justify">
                    {paragraph}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          CONCLUSION - Cyrille
          ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-lilac-50/50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className={`bg-white rounded-2xl sm:rounded-3xl p-8 sm:p-12 shadow-medium border border-lilac-100 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
              {/* Author */}
              <div className="text-center mb-6">
                <span className="text-gold-600 font-montserrat font-semibold text-sm sm:text-base">
                  {data.conclusion.author} :
                </span>
              </div>
              
              {/* Quote */}
              <div className="relative">
                <Quote className="absolute -top-2 -left-2 text-lilac-200 w-8 h-8 sm:w-12 sm:h-12 rotate-180" />
                <p className="text-base sm:text-lg lg:text-xl text-plum-800 font-montserrat leading-relaxed text-center px-4 sm:px-8 italic">
                  {data.conclusion.text}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          FINAL QUOTE
          ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-lilac-100 via-lilac-200 to-lilac-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Heart className="mx-auto text-gold-500 fill-gold-500 w-10 h-10 sm:w-14 sm:h-14 mb-6 sm:mb-8" />
            <p className="text-2xl sm:text-3xl lg:text-4xl font-playfair font-bold text-plum-900 italic max-w-2xl mx-auto px-4">
              "{data.finalQuote}"
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurStory;
